import { useEffect, useState } from "react";
import axios from "axios";
import { Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SastWindowBadge — tiny live chip in the terminal header that mirrors the
 * backend `/api/withdrawal/window` state so clients always see where they
 * are in the South Africa payout window (10:00–18:00 SAST).
 *
 * Open  → "Payouts open · closes 18:00 SAST"  (teal pulse)
 * Shut  → "Payouts reopen at 10:00 SAST"       (amber)
 */
export default function SastWindowBadge() {
  const [state, setState] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/withdrawal/window`);
        if (alive) setState(data);
      } catch {
        if (alive) setState(null);
      }
    };
    load();
    const id = setInterval(load, 60_000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (!state) return null;

  const open = !!state.is_open;
  const close = String(state.close_hour ?? 18).padStart(2, "0");
  const openH = String(state.open_hour ?? 10).padStart(2, "0");
  const label = open
    ? `Payouts open · closes ${close}:00 SAST`
    : `Payouts reopen at ${openH}:00 SAST`;

  return (
    <span
      data-testid="sast-window-badge"
      data-state={open ? "open" : "shut"}
      className={`ml-3 inline-flex items-center gap-1.5 self-center rounded-full border px-2.5 py-1 text-[9px] uppercase tracking-[0.2em] ${
        open
          ? "border-[#26A69A]/40 bg-[#26A69A]/10 text-[#26A69A]"
          : "border-amber-500/40 bg-amber-500/10 text-amber-300"
      }`}
      title={`South Africa office hours · ${openH}:00–${close}:00 SAST`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${
          open ? "bg-[#26A69A] animate-pulse-dot" : "bg-amber-400"
        }`}
      />
      <Clock className="h-3 w-3" />
      {label}
    </span>
  );
}
